import React, { useMemo, useRef, useState } from "react";
import videoStyles from "./video.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { isApiFileUrl, resolveFileUrl } from "../../utils/fileUrl";

function isYouTubeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return (
      host.includes("youtube.com") ||
      host === "youtu.be"
    );
  } catch {
    return false;
  }
}

function isVimeoUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname.toLowerCase().includes("vimeo.com");
  } catch {
    return false;
  }
}

function toYouTubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();

    // Shorts
    if (u.pathname.startsWith("/shorts/")) {
      const id = u.pathname.split("/")[2];
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }

    // youtu.be/<id>
    if (host === "youtu.be") {
      const id = u.pathname.replace("/", "");
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }

    // youtube.com/watch?v=<id>
    const vParam = u.searchParams.get("v");
    if (vParam) {
      return `https://www.youtube-nocookie.com/embed/${vParam}`;
    }

    // youtube.com/embed/<id>
    if (u.pathname.startsWith("/embed/")) {
      return `https://www.youtube-nocookie.com${u.pathname}${u.search}`;
    }

    // Fallback: try last segment as id
    const segments = u.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    return last ? `https://www.youtube-nocookie.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

function toVimeoEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    // Handle already-embedded URLs
    if (u.hostname.startsWith("player.vimeo.com") && u.pathname.startsWith("/video/")) {
      return u.toString();
    }
    // Extract numeric id from paths like /12345 or /channels/xyz/12345
    const match = u.pathname.match(/\/(\d+)/);
    const id = match?.[1];
    return id ? `https://player.vimeo.com/video/${id}` : null;
  } catch {
    return null;
  }
}

const Video: React.FC<{ src: string; title?: string }> = ({ src, title }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [videoAlreadyPlayed, setVideoAlreadyPlayed] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canPlay, setCanPlay] = useState<boolean>(false);

  const isYouTube = useMemo(() => isYouTubeUrl(src), [src]);
  const isVimeo = useMemo(() => isVimeoUrl(src), [src]);

  const embedSrc = useMemo(() => {
    if (isYouTube) return toYouTubeEmbedUrl(src);
    if (isVimeo) return toVimeoEmbedUrl(src);
    return null;
  }, [isYouTube, isVimeo, src]);

  const togglePlay = async () => {
    if (videoRef.current) {
      setVideoAlreadyPlayed(true); // Show controls immediately when user interacts
      if (isPlaying) {
        await videoRef.current.pause();
        setIsLoading(false);
      } else {
        setIsLoading(true);
        try {
          await videoRef.current.play();
          setIsLoading(false);
        } catch (error) {
          console.error('Video play error:', error);
          setIsLoading(false);
          setHasError(true);
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  if (embedSrc) {
    if (hasError) {
      return (
        <div 
          className={videoStyles.video}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            color: '#666',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          {t("CANT_LOAD_VIDEO")}
        </div>
      );
    }

    return (
      <iframe
        className={videoStyles.video}
        src={`${embedSrc}?rel=0&modestbranding=1`}
        title={title || "Exercise video"}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        onError={() => setHasError(true)}
      />
    );
  }

  if (hasError) {
    return (
      <div 
        className={videoStyles.video}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          color: '#666',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px',
          boxSizing: 'border-box'
        }}
      >
        {t("CANT_LOAD_VIDEO")}
      </div>
    );
  }

  return (
    <div className={videoStyles.videoWrapper}>
      {(() => {
        const resolvedSrc = resolveFileUrl(src) ?? src;
        const crossOrigin: "use-credentials" | "anonymous" = isApiFileUrl(resolvedSrc)
          ? "use-credentials"
          : "anonymous";
        return (
      <video 
        ref={videoRef} 
        className={videoStyles.video} 
        crossOrigin={crossOrigin} 
        controls={videoAlreadyPlayed} 
        playsInline 
        preload="metadata"
        onError={() => setHasError(true)}
        onLoadStart={() => setIsLoading(true)}
        onCanPlay={() => {
          setCanPlay(true);
          if (!isPlaying) {
            setIsLoading(false);
          }
        }}
        onWaiting={() => setIsLoading(true)}
        onPlaying={() => setIsLoading(false)}
        onLoadedData={() => setIsLoading(false)}
        onClick={() => {
          if (!videoAlreadyPlayed) {
            setVideoAlreadyPlayed(true);
          }
        }}
        onPlay={() => {
          setIsPlaying(true);
          setVideoAlreadyPlayed(true);
        }}
        onPause={() => setIsPlaying(false)}
      >
        <source src={resolvedSrc} type="video/mp4" />
        <source src={resolvedSrc} type="video/webm" />
        <source src={resolvedSrc} type="video/ogg" />
        Your browser does not support the video tag.
      </video>
        );
      })()}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className={videoStyles.loadingIndicator}>
          <div className={videoStyles.spinner}></div>
        </div>
      )}
      
      {/* Play button - only show when not loading and not already played */}
      {!videoAlreadyPlayed && !isLoading && (
        <button
          className={`${videoStyles.playBtn} ${isPlaying && videoStyles.isPlaying}`}
          onClick={togglePlay}
          aria-label="Play video"
        />
      )}
    </div>
  );
};

export default Video;
